/**
 * PUBLICACIÓN EN DOS CAMINOS (3-oct-2026).
 *
 * Rais: «la publicación, simplemente generar dos opciones. Una, tradicional, y la otra,
 * independiente». Cada camino prepara lo suyo de golpe, con el coste delante; lo que sale son Word en
 * la carpeta del libro. Qué entra en cada camino y qué se puede lanzar lo decide logica.ts
 * (`CAMINOS_DE_PUBLICACION`, `loteDeEncargos`); aquí solo se pinta y se lanza.
 */
import { useEffect, useState } from "react";
import { catalogoModelos, dolares, encargos as pedirCatalogo, modelosAsignados, mostrarEnCarpeta, pedirEncargo } from "../api";
import type { Encargo, ModeloCatalogo, Trabajo } from "../api";
import { CAMINOS_DE_PUBLICACION, costeDelLote, loteDeEncargos, nombreDeRuta } from "../logica";
import type { CaminoDePublicacion } from "../logica";

export default function Publicar({ carpeta, doc, trabajos, pulso, alLanzar }: {
  carpeta: string; doc: string | null; trabajos: Trabajo[]; pulso?: number; alLanzar: () => void;
}) {
  const [lista, setLista] = useState<Encargo[] | null | undefined>(undefined);
  const [precios, setPrecios] = useState<Record<string, ModeloCatalogo | undefined>>({});
  const [lanzando, setLanzando] = useState<CaminoDePublicacion | null>(null);
  const [fallo, setFallo] = useState<{ camino: CaminoDePublicacion; texto: string } | null>(null);
  const vivos = trabajos.filter((t) => t.tipo.startsWith("encargo-") && t.estado === "en_curso").length;

  useEffect(() => {
    if (!carpeta) return;
    pedirCatalogo(carpeta, doc).then(setLista).catch(() => setLista(undefined));
  }, [carpeta, doc, pulso, vivos]);
  useEffect(() => {
    Promise.all([modelosAsignados(), catalogoModelos()]).then(([m, c]) => {
      const de = (ag: string) => c.modelos.find((x) => x.id === (m.porAgente ?? {})[ag]);
      setPrecios({ publicar: de("publicar"), coach: de("coach"), author: de("author") });
    }).catch(() => {});
  }, []);

  if (lista === null) {
    return (
      <section className="carta">
        <h2>Publicación</h2>
        <div className="aviso">Tu motor todavía no sabe preparar estos documentos: actualízalo desde Ajustes.</div>
      </section>
    );
  }

  async function preparar(camino: CaminoDePublicacion) {
    const { lanzables } = loteDeEncargos(CAMINOS_DE_PUBLICACION[camino].ids, lista ?? [], trabajos);
    setLanzando(camino); setFallo(null);
    for (const p of lanzables) {
      try { await pedirEncargo(p.id, carpeta, doc, p.datos, false); alLanzar(); }
      catch (e) { setFallo({ camino, texto: `${p.titulo}: ${(e as Error).message}` }); break; }
    }
    setLanzando(null);
  }

  return (
    <div className="rejilla caminos">
      {(Object.keys(CAMINOS_DE_PUBLICACION) as CaminoDePublicacion[]).map((camino) => {
        const c = CAMINOS_DE_PUBLICACION[camino];
        const lote = loteDeEncargos(c.ids, lista ?? [], trabajos);
        const coste = costeDelLote(lote.lanzables, precios);
        return (
          <section className="carta" key={camino}>
            <h2>{c.titulo}<span className="sub">{lote.hechos} de {lote.piezas.length} hechos</span></h2>
            <p className="vacio">{c.que}</p>
            <div className="lista-etapa">
              {lote.piezas.map((p) => (
                <div className="fila-etapa" key={p.id}>
                  <div className="que">
                    <b>{p.titulo}</b>
                    <span className="vacio">
                      {p.estado === "en_marcha" ? "en marcha, en segundo plano…"
                        : p.estado === "hecho" && p.ruta ? <>En tu carpeta: <span className="mono">{nombreDeRuta(p.ruta)}</span>{" "}
                            <button className="btn texto" onClick={() => mostrarEnCarpeta(p.ruta!)}>Enseñar</button>
                            {/* M-7: la hoja de Excel para llevar los envíos, junto al Word. */}
                            {p.excel && <>{" · "}y su hoja de Excel: <span className="mono">{nombreDeRuta(p.excel)}</span>{" "}
                              <button className="btn texto" onClick={() => mostrarEnCarpeta(p.excel!)}>Enseñar</button></>}</>
                        : p.estado === "pide" ? `Antes hay que decir: ${p.pide}. Pídelo desde «Todos los encargos», más abajo.`
                        : p.estado === "fallido" ? `no se pudo terminar${p.error ? `: ${p.error}` : ""}`
                        : p.estado === "no_disponible" ? "tu motor todavía no lo hace: actualízalo desde Ajustes"
                        : "sin hacer"}
                    </span>
                  </div>
                  {p.estado === "hecho" && <span className="chip-estado ok">hecho</span>}
                  {p.estado === "en_marcha" && <span className="chip-estado">en marcha</span>}
                </div>
              ))}
            </div>
            {camino === "tradicional" && (
              <p className="vacio">
                {lote.piezas.find((p) => p.id === "editoriales")?.conExcel
                  ? "Con la lista de editoriales sale una hoja de Excel para llevar los envíos: una fila por editorial, por prioridad, y columnas para apuntar fechas y respuestas. Comprueba cada contacto en la web de la editorial antes de enviar."
                  : "La lista de editoriales sale como tabla en Word. La hoja de Excel para llevar los envíos llega con la próxima versión del motor."}
              </p>
            )}
            {fallo?.camino === camino && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo.texto}</p>}
            {lote.lanzables.length > 0 && (
              <div className="pie-form">
                <p className="vacio">{coste != null ? `~${dolares(coste)} de tu llave` : "Se paga con tu llave"} · se guardan en Word en la carpeta de tu libro.</p>
                <button className="btn" disabled={!doc || lanzando !== null} onClick={() => preparar(camino)}>
                  {lanzando === camino ? "Lanzando…" : lote.hechos > 0 || lote.enMarcha > 0 ? `Preparar lo que falta (${lote.lanzables.length})` : "Prepararlo todo"}
                </button>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
